import { Album } from './album';
import { Track } from '../trackList/track';
import { API_BASE, asArray } from '../../../config/api';

// Matches by album id, not by title (+ an artist-name qualifier to
// disambiguate same-titled albums by different artists) - now that every
// Track_API row carries a real album_id, id equality is both simpler and
// strictly more correct: it also handles two different artists who happen
// to have released an album with the same title, which title-string
// matching alone couldn't.
export function getTracksForAlbum(tracks: Track[], album: Album): Track[] {
    return tracks
        .filter((t) => t.getAlbumId() === album.getId())
        .sort((a, b) => (a.getTrackNumber() ?? 0) - (b.getTrackNumber() ?? 0));
}

// Same membership check getTracksForAlbum's filter uses, without building
// the whole track list - for deciding whether a given album is the one
// currently loaded.
export function isTrackInAlbum(track: Track | undefined | null, album: Album): boolean {
    if (!track) return false;
    return track.getAlbumId() === album.getId();
}

export async function getAlbumList(): Promise<Album[]> {
    try {
        const response = await fetch(`${API_BASE}/api/album`);
        const result = await response.json();

        if (!response.ok) {
            throw new Error(`Error fetching album list: ${result.message || response.statusText}`);
        }

        return asArray<any>(result).map((a: any) => Album.fromJson(a));
    } catch (error) {
        console.error(error);
        return [];
    }
}

export async function getRecentAlbums(limit: number): Promise<Album[]> {
    try {
        const response = await fetch(`${API_BASE}/api/album?sort=recent&limit=${limit}`);
        const result = await response.json();

        if (!response.ok) {
            throw new Error(`Error fetching recent albums: ${result.message || response.statusText}`);
        }

        return asArray<any>(result).map((a: any) => Album.fromJson(a));
    } catch (error) {
        console.error(error);
        return [];
    }
}

// getAlbumsByArtistID fetches every album this artist is credited on (via
// album_artist - collaborations/compilation appearances included), by ID
// rather than matching album/artist names client-side.
export async function getAlbumsByArtistID(artistId: number): Promise<Album[]> {
    try {
        const response = await fetch(`${API_BASE}/api/album?artistId=${artistId}`);
        const result = await response.json();

        if (!response.ok) {
            throw new Error(`Error fetching albums for artist: ${result.message || response.statusText}`);
        }

        return Array.isArray(result) ? result.map((a: any) => Album.fromJson(a)) : [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export async function getAlbumByID(id: number): Promise<Album | null> {
    try {
        const response = await fetch(`${API_BASE}/api/album?id=${id}`);
        const result = await response.json();

        if (!response.ok) {
            throw new Error(`Error fetching album: ${result.message || response.statusText}`);
        }

        return Album.fromJson(result);
    } catch (error) {
        console.error(error);
        return null;
    }
}
